import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { EXTENDED_SESSION_CONFIG } from '../auth/AuthContext';

/**
 * Custom hook to manage auth status, verification UI, and inactivity logout
 * @param {Object} auth - Auth context from useAuth()
 * @returns {Object} - Auth status and verification state
 */
const useAuthStatus = ({ user, loading, logout }) => {
  const location = useLocation();
  const [showVerifyPopup, setShowVerifyPopup] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isOAuthRedirect, setIsOAuthRedirect] = useState(false);
  
  // Use centralized extended session configuration (8 hours)
  const INACTIVITY_TIMEOUT = EXTENDED_SESSION_CONFIG.INACTIVITY_TIMEOUT_MS;

  // Check if this is an OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isOAuth = params.has('access_token') && params.has('id_token') && params.has('oauth_success');
    if (isOAuth) {
      setIsOAuthRedirect(true);
    }
  }, [location.search]);

  // Inactivity Logout Logic (Extended to 8 hours for better UX)
  useEffect(() => {
    if (!user) return; // Only track inactivity for logged in users
    
    let timeout;
    const resetTimer = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        logout();
      }, INACTIVITY_TIMEOUT);
    };
    
    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keydown", resetTimer);
    window.addEventListener("touchstart", resetTimer);
    
    resetTimer();
    
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keydown", resetTimer);
      window.removeEventListener("touchstart", resetTimer);
    };
  }, [logout, user, INACTIVITY_TIMEOUT]);

  return {
    showVerifyPopup,
    setShowVerifyPopup,
    registeredEmail, 
    setRegisteredEmail,
    isRedirecting,
    setIsRedirecting,
    isOAuthRedirect,
    isLoading: loading || isRedirecting
  };
};

export default useAuthStatus;